import { Address, DrAccount } from "@/services/model";

/** Joins the non-empty parts with a space; `0` (house number, zip) counts as a value. */
export const joinParts = (...parts: (string | number | undefined | null)[]) =>
  parts.filter((part) => part != null && `${part}`.trim() !== "").join(" ");

/** The backend prepends "Vertragskonto 2 - " to the Bezeichnung. */
const ACCOUNT_NUMBER_PREFIX = /^\s*Vertragskonto\s+\d+\s*[-–:]?\s*/i;

/** The Bezeichnung as entered, without the backend's "Vertragskonto X" prefix. */
export const getBillingAccountAlias = (account: DrAccount) =>
  (account.billingRecipient ?? "").replace(ACCOUNT_NUMBER_PREFIX, "");

export const getBillingAccountName = (account: DrAccount) =>
  getBillingAccountAlias(account) || account.billToAddress?.companyName || "";

const getBillingAccountDigits = (account: DrAccount) =>
  account.billingAccount?.match(/\d+/)?.[0];

/** "Vertragskonto 2" or "VK-002" becomes "RE-002". */
export const getBillingAccountNumber = (account: DrAccount) => {
  const digits = getBillingAccountDigits(account);

  return digits
    ? `RE-${digits.padStart(3, "0")}`
    : (account.billingAccount ?? "");
};

const getBillingAccountOrder = (account: DrAccount) => {
  const digits = getBillingAccountDigits(account);

  return digits == null ? Number.MAX_SAFE_INTEGER : Number(digits);
};

/** Ascending by Vertragskonto number (RE-001 first); accounts without one go last. */
export const sortBillingAccounts = <T extends DrAccount>(accounts: T[]) =>
  [...accounts].sort(
    (first, second) =>
      getBillingAccountOrder(first) - getBillingAccountOrder(second),
  );

/** "RE-004 – Firmenname", how a Vertragskonto is picked in a select. */
export const getBillingAccountLabel = (account: DrAccount) =>
  [getBillingAccountNumber(account), getBillingAccountName(account)]
    .filter(Boolean)
    .join(" – ");

/** Combobox `displayValue` of a Vertragskonto; empty while none is picked. */
export const getBillingAccountDisplayValue = (account?: DrAccount | null) =>
  account ? getBillingAccountLabel(account) : "";

/** Company, contact person and address supplement, one entry per line. */
export const getRecipientLines = (
  address?: Pick<
    Address,
    "companyName" | "firstName" | "lastName" | "postalDeliveryNotes"
  >,
) =>
  [
    address?.companyName,
    joinParts(address?.firstName, address?.lastName),
    address?.postalDeliveryNotes,
  ].filter((line): line is string => !!line);

export interface BillingAccountSubscription {
  /** e.g. "0761033493 - Baustelle Dübendorf WALO". */
  label: string;
  /** Without it the Abo cannot be moved. */
  simId?: number;
}

/** Router state the Sammelverschiebung page is opened with. */
export interface BulkMoveLocationState {
  sourceId: number;
  simIds: number[];
  /** Where Abbrechen and a successful move lead back to. */
  returnTo: string;
}

/** "Rufnummer - Alias", or just the one that is set. */
export const getSubscriptions = (
  account: DrAccount,
): BillingAccountSubscription[] =>
  (account.contractAlias ?? []).map(({ serviceId, msisdn, alias }) => ({
    label: [msisdn, alias].filter(Boolean).join(" - "),
    // The service id of an Abo is the SIM id the transfer endpoint expects.
    simId: serviceId,
  }));

/** The SIM ids of the movable Abos, as checkbox values. */
export const getMovableSimIds = (subscriptions: BillingAccountSubscription[]) =>
  subscriptions
    .filter(({ simId }) => simId != null)
    .map(({ simId }) => `${simId}`);
